import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

/**
 * How long the `authStatus === 'configuring'` spinner may persist before the
 * loader switches from a bare spinner to a user-visible escape hatch.
 *
 * The Amplify Authenticator state machine normally settles `configuring` into a
 * terminal `authenticated` / `unauthenticated` transition within a second or
 * two. When an idle-return token refresh hangs (issue #2595) it can sit in
 * `configuring` indefinitely, leaving the user staring at an unbounded spinner.
 * After this delay we surface a "Sign in" CTA so the user is never stuck — this
 * is the manual complement to the programmatic auto-recovery in
 * `SessionRecovery`.
 */
export const CONFIGURING_CTA_DELAY_MS = 10_000;

/**
 * Shared loading view rendered while the Amplify `authStatus` is `'configuring'`.
 *
 * Used by BOTH spinner sites — `App.tsx`'s top-level `AppRoutes` and the
 * per-route `ProtectedRoute` — so the two never drift. It shows a spinner first
 * ("Loading…") and, if `configuring` persists beyond {@link CONFIGURING_CTA_DELAY_MS},
 * reveals a brief message plus a manual "Sign in" escape-hatch button. Clicking
 * the button performs a local `signOut()` then navigates to `/login`, mirroring
 * `AuthenticatedLayout`'s sign-out handler.
 *
 * This component renders inside the always-mounted `<Router>` (it calls
 * `useNavigate`), so it must never be hoisted above the Router.
 */
function ConfiguringLoader() {
    const navigate = useNavigate();
    const [showCta, setShowCta] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setShowCta(true), CONFIGURING_CTA_DELAY_MS);
        return () => clearTimeout(timer);
    }, []);

    const handleSignIn = async () => {
        try {
            await signOut();
        } catch (error) {
            // A hung/failed session is exactly the case we're recovering from;
            // surface-to-login regardless of whether signOut itself succeeded.
            console.error('Error signing out from configuring loader:', error);
        }
        navigate('/login');
    };

    return (
        <Box
            role="status"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px',
                gap: 2,
            }}
        >
            <CircularProgress />
            {showCta ? (
                <>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        This is taking longer than expected.
                    </Typography>
                    <Button variant="contained" onClick={handleSignIn}>
                        Sign in
                    </Button>
                </>
            ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Loading...
                </Typography>
            )}
        </Box>
    );
}

export default ConfiguringLoader;
